import React, { useEffect, useState } from 'react';
import { List, Typography, Button, Alert, Card, Badge, Tag, Empty } from 'antd';
import { BellOutlined, CheckOutlined } from '@ant-design/icons';
import { getNotifications, markNotificationRead, markAllNotificationsRead } from '../services/api';

const { Title, Text } = Typography;

interface Notification {
  id: string;
  notification_type: string;
  title: string;
  message: string;
  channel: string;
  is_read: boolean;
  created_at: string;
}

const typeColor: { [key: string]: string } = {
  ASSIGNMENT: 'blue',
  REVIEW_REQUIRED: 'purple',
  APPROVAL_REQUIRED: 'green',
  REMINDER: 'cyan',
  TAT_BREACH: 'red',
  ESCALATION: 'volcano',
  ACK_REQUIRED: 'orange',
  STATUS_CHANGE: 'default',
  DRAWING_REVISED: 'geekblue',
};

const NotificationsPage: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const response = await getNotifications();
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unread_count);
    } catch (err) {
      setError('Failed to load notifications.');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (id: string) => {
    try {
      await markNotificationRead(id);
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      setError('Failed to mark notification as read.');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      setError('Failed to mark all notifications as read.');
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Title level={4} style={{ margin: 0, color: '#1B2A4A' }}>
            Notifications
          </Title>
          {unreadCount > 0 && (
            <Badge count={unreadCount} style={{ backgroundColor: '#ff4d4f' }} />
          )}
        </div>
        {unreadCount > 0 && (
          <Button
            icon={<CheckOutlined />}
            onClick={handleMarkAllRead}
            style={{ borderColor: '#1B2A4A', color: '#1B2A4A' }}
          >
            Mark All Read
          </Button>
        )}
      </div>

      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 24 }} />}

      <Card style={{ borderRadius: 12 }}>
        {notifications.length === 0 ? (
          <Empty
            image={<BellOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />}
            description="No notifications yet"
          />
        ) : (
          <List
            loading={loading}
            dataSource={notifications}
            renderItem={(item) => (
              <List.Item
                style={{
                  background: item.is_read ? 'white' : '#f0f7ff',
                  padding: '16px',
                  borderRadius: 8,
                  marginBottom: 8,
                  border: item.is_read ? '1px solid #f0f0f0' : '1px solid #bae0ff',
                }}
                actions={[
                  !item.is_read && (
                    <Button
                      size="small"
                      type="link"
                      onClick={() => handleMarkRead(item.id)}
                    >
                      Mark Read
                    </Button>
                  )
                ].filter(Boolean)}
              >
                <List.Item.Meta
                  avatar={
                    <Badge dot={!item.is_read} color="blue">
                      <BellOutlined style={{ fontSize: 20, color: '#2E75B6' }} />
                    </Badge>
                  }
                  title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Text strong>{item.title}</Text>
                      <Tag color={typeColor[item.notification_type] || 'default'} style={{ fontSize: 11 }}>
                        {item.notification_type.replace('_', ' ')}
                      </Tag>
                    </div>
                  }
                  description={
                    <div>
                      <Text type="secondary" style={{ fontSize: 13 }}>{item.message}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {new Date(item.created_at).toLocaleString('en-IN')}
                      </Text>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Card>
    </div>
  );
};

export default NotificationsPage;